import { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select } from '../ui/select';
import { Badge } from '../ui/badge';
import { DatePicker } from '../ui/DatePicker';

const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3010/api';

const numericFilter = (value) => value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');

// Sum editable legs into trip totals, matching the server's rounding.
const sumTotals = (legs) => {
  let distance = 0, billable = 0, unbillable = 0, cost = 0;
  for (const l of legs) {
    distance += Number(l.distance) || 0;
    billable += parseFloat(l.billable_time) || 0;
    unbillable += parseFloat(l.unbillable_time) || 0;
    cost += parseFloat(l.cost) || 0;
  }
  return {
    distance: Math.round(distance * 10) / 10,
    billable_time: Math.round(billable * 10) / 10,
    unbillable_time: Math.round(unbillable * 10) / 10,
    cost: Math.round(cost * 100) / 100,
  };
};

// Augment a server ticket with the local review fields the UI mutates.
const augment = (t) => ({
  ...t,
  _include: true,
  _day: t.created_date,
  _code: t.code || (t.suggested_codes && t.suggested_codes[0]) || '',
});

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const weekday = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  return isNaN(d.getTime()) ? '' : WEEKDAYS[d.getDay()];
};
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const monthLabel = (ym) => { const [y, mo] = ym.split('-'); return `${MONTHS[Number(mo) - 1]} ${y}`; };

const SuggestedTrips = ({ user, token, api, onPendingChange }) => {
  const [tickets, setTickets] = useState([]);
  const [existingTrips, setExistingTrips] = useState([]);
  const [validCodes, setValidCodes] = useState([]);
  const [allCodes, setAllCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [monthFilter, setMonthFilter] = useState('all');

  // Per-ticket expand-for-details (lazy-loaded full comment thread)
  const [expanded, setExpanded] = useState({});
  const [details, setDetails] = useState({});

  // Archived (dismissed) tickets — collapsible, lazy-loaded
  const [showArchived, setShowArchived] = useState(false);
  const [archived, setArchived] = useState(null);
  const [archivedLoading, setArchivedLoading] = useState(false);

  // Active build (one day at a time)
  const [previewDay, setPreviewDay] = useState(null);
  const [preview, setPreview] = useState(null);
  const [editLegs, setEditLegs] = useState(null); // editable copy of the calculated legs
  const [buildStatus, setBuildStatus] = useState('');
  const [isBuilding, setIsBuilding] = useState(false);
  const buildMetaRef = useRef({ ticketIds: [], learned: [] });
  const previewRef = useRef(null);

  const validSet = useMemo(() => new Set(validCodes), [validCodes]);

  const reportPending = (list) => { if (onPendingChange) onPendingChange(list.length); };

  const loadExisting = async () => {
    try {
      const trips = await api.get('/trips', token);
      setExistingTrips(trips || []);
    } catch {
      /* existing trips are supplementary; ignore failure */
    }
  };

  const load = async (force) => {
    setLoading(true);
    setError('');
    try {
      if (force) await api.post('/syncro/sync', { force: true }, token);
      const data = await api.get('/syncro/suggestions', token);
      setTickets(data.tickets.map(augment));
      setValidCodes(data.valid_codes || []);
      setAllCodes(data.all_codes || []);
      reportPending(data.tickets);
      loadExisting();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(false); /* eslint-disable-next-line */ }, []);

  // Bring the build area into view when a build starts and when its preview arrives.
  useEffect(() => {
    if (previewDay && previewRef.current) {
      previewRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [previewDay, preview]);

  const patchTicket = (id, patch) => {
    setTickets(prev => prev.map(t => (t.syncro_ticket_id === id ? { ...t, ...patch } : t)));
  };

  // Group tickets by their chosen day, preserving array order within a day.
  const groups = useMemo(() => {
    const g = {};
    tickets.forEach(t => { (g[t._day] = g[t._day] || []).push(t); });
    return g;
  }, [tickets]);
  const days = useMemo(() => Object.keys(groups).sort().reverse(), [groups]);
  const months = useMemo(() => [...new Set(days.map(d => d.slice(0, 7)))].sort().reverse(), [days]);
  const visibleDays = useMemo(
    () => (monthFilter === 'all' ? days : days.filter(d => d.startsWith(monthFilter))),
    [days, monthFilter]
  );

  // Existing (committed) trips, scoped to the months that have suggestions and the active filter.
  const existingByDate = useMemo(() => {
    const monthsToShow = monthFilter === 'all' ? months : [monthFilter];
    const g = {};
    for (const tr of existingTrips) {
      if (!tr.trip_date || !monthsToShow.includes(tr.trip_date.slice(0, 7))) continue;
      (g[tr.trip_date] = g[tr.trip_date] || []).push(tr);
    }
    return g;
  }, [existingTrips, months, monthFilter]);

  // Days that already have a committed trip — a dot in the date picker (all months, since the
  // calendar can navigate freely). Label lists each day's route(s) for the hover tooltip.
  const tripDots = useMemo(() => {
    const byDate = {};
    for (const tr of existingTrips) {
      if (!tr.trip_date) continue;
      const d = tr.trip_date.slice(0, 10);
      const route = tr.stops ? tr.stops.split(',').join(' → ') : 'HOM';
      (byDate[d] = byDate[d] || []).push(route);
    }
    return Object.entries(byDate).map(([date, routes]) => ({ date, label: routes.join('; ') }));
  }, [existingTrips]);

  // Merge suggested days + existing-trip days into one chronological timeline (newest first).
  const timelineDates = useMemo(() => {
    const set = new Set(visibleDays);
    Object.keys(existingByDate).forEach(d => set.add(d));
    return [...set].sort().reverse();
  }, [visibleDays, existingByDate]);

  // Move a ticket up/down among its same-day neighbours in the master array.
  const move = (id, dir) => {
    setTickets(prev => {
      const arr = [...prev];
      const idx = arr.findIndex(t => t.syncro_ticket_id === id);
      if (idx < 0) return prev;
      const day = arr[idx]._day;
      let swap = idx + dir;
      while (swap >= 0 && swap < arr.length && arr[swap]._day !== day) swap += dir;
      if (swap < 0 || swap >= arr.length) return prev;
      [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
      return arr;
    });
  };

  const toggleDetails = async (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
    if (!details[id]) {
      setDetails(prev => ({ ...prev, [id]: { loading: true } }));
      try {
        const data = await api.get(`/syncro/ticket/${id}`, token);
        setDetails(prev => ({ ...prev, [id]: { loading: false, comments: data.comments } }));
      } catch (e) {
        setDetails(prev => ({ ...prev, [id]: { loading: false, error: e.message } }));
      }
    }
  };

  const loadArchived = async () => {
    setArchivedLoading(true);
    try {
      const data = await api.get('/syncro/archived', token);
      setArchived(data.tickets);
    } catch (e) {
      setError(e.message);
    } finally {
      setArchivedLoading(false);
    }
  };

  const toggleArchived = () => {
    const next = !showArchived;
    setShowArchived(next);
    if (next && archived === null) loadArchived();
  };

  const dismiss = async (id) => {
    try {
      await api.post('/syncro/resolve', { ticket_ids: [id], status: 'dismissed' }, token);
      setTickets(prev => {
        const next = prev.filter(t => t.syncro_ticket_id !== id);
        reportPending(next);
        return next;
      });
      // Archived list is now stale — refresh it if open, else drop the cache
      if (showArchived) loadArchived(); else setArchived(null);
    } catch (e) {
      setError(e.message);
    }
  };

  const restore = async (id) => {
    try {
      await api.post('/syncro/resolve', { ticket_ids: [id], status: 'pending' }, token);
      const t = (archived || []).find(x => x.syncro_ticket_id === id);
      setArchived(prev => (prev || []).filter(x => x.syncro_ticket_id !== id));
      if (t) {
        setTickets(prev => {
          const next = [augment(t), ...prev];
          reportPending(next);
          return next;
        });
      }
    } catch (e) {
      setError(e.message);
    }
  };

  const buildTrip = async (day) => {
    setError('');
    const dayTickets = tickets.filter(t => t._day === day && t._include);
    if (dayTickets.length === 0) { setError(`No stops selected for ${day}.`); return; }

    const noCode = dayTickets.filter(t => !t._code);
    if (noCode.length) { setError(`Pick a code for: ${[...new Set(noCode.map(t => t.customer_name))].join(', ')}`); return; }

    const badAddr = dayTickets.filter(t => !validSet.has(t._code));
    if (badAddr.length) {
      setError(`These codes have no address yet — add them in Address Mappings: ${[...new Set(badAddr.map(t => t._code))].join(', ')}`);
      return;
    }

    // Collapse multiple tickets for the same code into one stop, preserving order.
    const seen = new Set();
    const destinations = [];
    for (const t of dayTickets) {
      if (seen.has(t._code)) continue;
      seen.add(t._code);
      destinations.push({ id: t._code, description: '', billable_time: 0, unbillable_time: 0, cost: 0 });
    }

    buildMetaRef.current = {
      ticketIds: dayTickets.map(t => t.syncro_ticket_id),
      learned: dayTickets.map(t => ({ syncro_customer_id: t.customer_id, code: t._code })),
    };

    setPreviewDay(day);
    setPreview(null);
    setEditLegs(null);
    setIsBuilding(true);
    setBuildStatus('Calculating route...');

    try {
      const response = await fetch(`${API_BASE}/trips/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ destinations, no_mileage_entries: [] }),
      });
      const refreshedToken = response.headers.get('X-Refreshed-Token');
      if (refreshedToken) localStorage.setItem('milewise_token', refreshedToken);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.trim()) continue;
          const event = JSON.parse(line);
          if (event.type === 'progress') {
            setBuildStatus(event.message);
          } else if (event.type === 'error') {
            setError(event.error);
            setBuildStatus('');
          } else if (event.type === 'result') {
            setPreview({ ...event, trip_date: day });
            // Editable copy: blank numeric fields show as empty (placeholder 0)
            setEditLegs(event.legs.map(l => ({
              from_id: l.from_id, to_id: l.to_id,
              from_address: l.from_address, to_address: l.to_address,
              distance: l.distance,
              billable_time: l.billable_time ? String(l.billable_time) : '',
              unbillable_time: l.unbillable_time ? String(l.unbillable_time) : '',
              cost: l.cost ? String(l.cost) : '',
              description: l.description || '',
            })));
            setBuildStatus('');
          }
        }
      }
    } catch (e) {
      setError(e.message);
      setBuildStatus('');
    } finally {
      setIsBuilding(false);
    }
  };

  const updateLeg = (idx, field, value) => {
    setEditLegs(prev => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  };

  const commitTrip = async () => {
    if (!editLegs) return;
    try {
      setBuildStatus('Committing trip...');
      const legs = editLegs.map(l => ({
        from_id: l.from_id, to_id: l.to_id,
        from_address: l.from_address, to_address: l.to_address,
        distance: Number(l.distance) || 0,
        billable_time: parseFloat(l.billable_time) || 0,
        unbillable_time: parseFloat(l.unbillable_time) || 0,
        cost: parseFloat(l.cost) || 0,
        description: (l.description || '').trim(),
      }));
      const res = await api.post('/trips/commit', {
        trip_date: preview.trip_date,
        legs,
        no_mileage_entries: [],
        totals: sumTotals(editLegs),
      }, token);

      const { ticketIds, learned } = buildMetaRef.current;
      await api.post('/syncro/resolve', {
        ticket_ids: ticketIds,
        status: 'committed',
        committed_trip_id: res.id,
        learned,
      }, token);

      // Drop the committed tickets from the queue
      setTickets(prev => {
        const next = prev.filter(t => !ticketIds.includes(t.syncro_ticket_id));
        reportPending(next);
        return next;
      });
      setPreview(null);
      setEditLegs(null);
      setPreviewDay(null);
      setBuildStatus('');
      loadExisting(); // the new trip now shows in the timeline
    } catch (e) {
      setError(`Error committing: ${e.message}`);
      setBuildStatus('');
    }
  };

  const cancelBuild = () => { setPreview(null); setEditLegs(null); setPreviewDay(null); setBuildStatus(''); };

  if (loading) {
    return <div className="max-w-5xl mx-auto text-sm text-muted-foreground">Loading suggestions…</div>;
  }

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-lg font-bold">Suggested Trips</h2>
          <p className="text-sm text-muted-foreground">
            {tickets.length === 0
              ? 'No pending suggestions — all caught up.'
              : `${tickets.length} pending ticket${tickets.length === 1 ? '' : 's'} from Syncro to review.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {months.length > 0 && (
            <Select
              value={monthFilter}
              onChange={e => setMonthFilter(e.target.value)}
              className="h-9 w-[150px] text-sm"
              title="Filter by month"
            >
              <option value="all">All months</option>
              {months.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={() => load(true)} disabled={isBuilding}>
            Refresh from Syncro
          </Button>
        </div>
      </div>

      {error && !previewDay && <div className="text-sm font-medium text-destructive">{error}</div>}

      {/* Timeline: committed trips interleaved with suggested day-groups */}
      {timelineDates.map((day, idx) => {
        const dayTickets = groups[day];
        const includedCount = dayTickets ? dayTickets.filter(t => t._include).length : 0;
        const dayExisting = existingByDate[day] || [];
        const ym = day.slice(0, 7);
        const showMonthHeader = monthFilter === 'all' && (idx === 0 || timelineDates[idx - 1].slice(0, 7) !== ym);
        return (
          <Fragment key={day}>
            {showMonthHeader && (
              <div className="pt-4 flex items-center gap-3">
                <h3 className="font-display font-extrabold text-2xl text-primary uppercase tracking-wide">{monthLabel(ym)}</h3>
                <div className="flex-1 h-1 bg-primary rounded-full" />
              </div>
            )}
            {dayExisting.map(tr => {
              const hrs = Number(tr.total_billable_time) + Number(tr.total_unbillable_time);
              return (
                <div key={`e-${tr.id}`} className="rounded-lg border border-dashed border-emerald-500/50 bg-emerald-500/5 px-4 py-2.5">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge className="border-0 bg-emerald-500/20 text-emerald-500 text-[10px]">Existing trip</Badge>
                      <span className="text-sm font-medium whitespace-nowrap">{weekday(day)}, {day}</span>
                      <span className="text-xs text-muted-foreground truncate">{tr.stops ? tr.stops.split(',').join(' · ') : 'no stops'}</span>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {Number(tr.total_distance).toFixed(1)} mi · {hrs.toFixed(1)} hrs · ${Number(tr.total_cost).toFixed(2)}
                    </span>
                  </div>
                </div>
              );
            })}
            {dayTickets && (
          <Card>
            <CardHeader className="py-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base">
                  {weekday(day)}, {day} <span className="text-muted-foreground font-normal text-sm">· {includedCount} stop{includedCount === 1 ? '' : 's'} selected</span>
                </CardTitle>
                <Button size="sm" onClick={() => buildTrip(day)} disabled={isBuilding || includedCount === 0}>
                  {previewDay === day && isBuilding ? 'Building…' : 'Build Trip'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-1">
              {dayTickets.map((t, i) => {
                const codeOptions = [];
                const seenOpt = new Set();
                (t.suggested_codes || []).forEach(c => { if (!seenOpt.has(c)) { seenOpt.add(c); codeOptions.push(c); } });
                allCodes.forEach(c => { if (!seenOpt.has(c)) { seenOpt.add(c); codeOptions.push(c); } });
                const codeBad = t._code && !validSet.has(t._code);
                const hasResolvedAlt = t.resolved_date && t.resolved_date !== t.created_date;
                return (
                  <div key={t.syncro_ticket_id} className="border-b border-border/40 last:border-0">
                  <div className="flex flex-col gap-2 py-2 sm:flex-row sm:items-center sm:gap-2">
                    {/* checkbox + customer info */}
                    <div className="flex items-start gap-2 min-w-0 sm:flex-1">
                      <input
                        type="checkbox"
                        checked={t._include}
                        onChange={e => patchTicket(t.syncro_ticket_id, { _include: e.target.checked })}
                        className="h-4 w-4 mt-1 shrink-0 accent-[hsl(var(--primary))]"
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {t.customer_name}
                          <span className="text-muted-foreground font-normal"> · #{t.number} {t.subject}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {t.problem_type && <Badge variant="secondary" className="text-[10px]">{t.problem_type}</Badge>}
                          {t.asset_names && t.asset_names.length > 0 && (
                            <span className="text-[11px] text-muted-foreground truncate">{t.asset_names.join(', ')}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* controls — wrap below the info on mobile, inline on desktop */}
                    <div className="flex items-center gap-1.5 flex-wrap pl-6 sm:pl-0 sm:flex-nowrap sm:justify-end sm:shrink-0">
                      {/* trip-day pick: calendar with created/resolved highlighted; any date pickable */}
                      <DatePicker
                        value={t._day}
                        onChange={d => patchTicket(t.syncro_ticket_id, { _day: d })}
                        markers={[
                          { date: t.created_date, label: 'Created', ring: 'ring-2 ring-inset ring-blue-400', text: 'text-blue-400' },
                          ...(hasResolvedAlt ? [{ date: t.resolved_date, label: 'Resolved', ring: 'ring-2 ring-inset ring-emerald-400', text: 'text-emerald-400' }] : []),
                        ]}
                        dots={tripDots}
                      />
                      {/* code pick */}
                      <Select
                        value={t._code}
                        onChange={e => patchTicket(t.syncro_ticket_id, { _code: e.target.value })}
                        className={`h-9 sm:h-8 w-[104px] text-xs ${codeBad ? 'border-destructive text-destructive' : ''}`}
                        title={codeBad ? 'This code has no address mapping' : 'MileWise code'}
                      >
                        <option value="">— code —</option>
                        {codeOptions.map(c => <option key={c} value={c}>{c}</option>)}
                      </Select>
                      <button
                        onClick={() => toggleDetails(t.syncro_ticket_id)}
                        className="h-9 sm:h-8 text-xs text-muted-foreground hover:text-foreground whitespace-nowrap px-2 sm:px-1 rounded-md border border-border/60 sm:border-0"
                        title="Show ticket comments"
                      >
                        {expanded[t.syncro_ticket_id] ? '▾' : '▸'} Details
                      </button>
                      {/* reorder */}
                      <div className="flex flex-col">
                        <button onClick={() => move(t.syncro_ticket_id, -1)} disabled={i === 0} className="text-xs leading-none text-muted-foreground hover:text-foreground disabled:opacity-30 px-1" title="Move up">▲</button>
                        <button onClick={() => move(t.syncro_ticket_id, 1)} disabled={i === dayTickets.length - 1} className="text-xs leading-none text-muted-foreground hover:text-foreground disabled:opacity-30 px-1" title="Move down">▼</button>
                      </div>
                      <button onClick={() => dismiss(t.syncro_ticket_id)} className="text-muted-foreground hover:text-destructive text-base px-1.5" title="Archive (not a trip)">✕</button>
                    </div>
                  </div>
                  {expanded[t.syncro_ticket_id] && (
                    <div className="ml-6 mr-1 mb-2 rounded-md bg-muted/40 p-3 text-xs">
                      {(() => {
                        const det = details[t.syncro_ticket_id];
                        if (!det || det.loading) return <span className="text-muted-foreground">Loading ticket…</span>;
                        if (det.error) return <span className="text-destructive">Couldn’t load details: {det.error}</span>;
                        if (!det.comments || det.comments.length === 0) return <span className="text-muted-foreground">No comments on this ticket.</span>;
                        return (
                          <div className="space-y-2 max-h-72 overflow-y-auto">
                            {det.comments.map(c => (
                              <div key={c.id} className="border-b border-border/30 last:border-0 pb-2 last:pb-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className="font-semibold">{c.author}</span>
                                  {c.hidden && <Badge variant="secondary" className="text-[9px]">internal</Badge>}
                                  <span className="text-muted-foreground">{c.created_at ? c.created_at.slice(0, 16).replace('T', ' ') : ''}</span>
                                </div>
                                <div className="whitespace-pre-wrap text-foreground/90 leading-snug">{c.body}</div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  </div>
                );
              })}
              {previewDay === day && (
                <div ref={previewRef} className="mt-3 border-t pt-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">Trip for {weekday(day)}, {day}</span>
                    <Button variant="ghost" size="sm" onClick={cancelBuild}>Cancel</Button>
                  </div>
                  {buildStatus && <div className="text-sm text-blue-500">{buildStatus}</div>}
                  {error && <div className="text-sm text-destructive">{error}</div>}
                  {editLegs && (() => {
                    const tt = sumTotals(editLegs);
                    return (
                      <>
                        <p className="text-xs text-muted-foreground">Enter billable/unbillable hours, cost, and a description for each stop, then commit.</p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm min-w-[640px]">
                            <thead>
                              <tr className="border-b text-left text-muted-foreground">
                                <th className="pb-1 pr-2">Route</th>
                                <th className="pb-1 pr-2">Distance</th>
                                <th className="pb-1 pr-2 w-20">Billable</th>
                                <th className="pb-1 pr-2 w-20">Unbillable</th>
                                <th className="pb-1 pr-2 w-24">Cost</th>
                                <th className="pb-1">Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {editLegs.map((l, idx) => {
                                const isReturn = l.to_id === 'HOM';
                                return (
                                  <tr key={idx} className="border-b border-border/50">
                                    <td className="py-1 pr-2 whitespace-nowrap">{l.from_id} &rarr; {l.to_id}</td>
                                    <td className="py-1 pr-2 whitespace-nowrap text-muted-foreground">{Number(l.distance).toFixed(2)} mi</td>
                                    {isReturn ? (
                                      <td className="py-1 pr-2 text-muted-foreground" colSpan={4}>Return home — no time/cost</td>
                                    ) : (
                                      <>
                                        <td className="py-1 pr-2"><Input value={l.billable_time} onChange={e => updateLeg(idx, 'billable_time', numericFilter(e.target.value))} placeholder="0.0" className="h-8 text-sm" /></td>
                                        <td className="py-1 pr-2"><Input value={l.unbillable_time} onChange={e => updateLeg(idx, 'unbillable_time', numericFilter(e.target.value))} placeholder="0.0" className="h-8 text-sm" /></td>
                                        <td className="py-1 pr-2"><Input value={l.cost} onChange={e => updateLeg(idx, 'cost', numericFilter(e.target.value))} placeholder="0.00" className="h-8 text-sm" /></td>
                                        <td className="py-1"><Input value={l.description} onChange={e => updateLeg(idx, 'description', e.target.value)} placeholder="Description" className="h-8 text-sm min-w-[140px]" /></td>
                                      </>
                                    )}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div className="text-sm font-semibold space-y-0.5">
                          {tt.distance > 0 && <div>Total Distance: {tt.distance.toFixed(2)} miles</div>}
                          <div>Total Time: {(tt.billable_time + tt.unbillable_time).toFixed(1)} hours ({tt.billable_time} billable + {tt.unbillable_time} unbillable)</div>
                          <div>Total Cost: ${tt.cost.toFixed(2)}</div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={commitTrip}>Commit Trip</Button>
                          {preview && preview.maps_url && (
                            <Button variant="outline" size="sm" onClick={() => window.open(preview.maps_url, '_blank')}>View Maps</Button>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </CardContent>
          </Card>
            )}
          </Fragment>
        );
      })}

      {monthFilter !== 'all' && visibleDays.length === 0 && tickets.length > 0 && (
        <div className="text-sm text-muted-foreground">No pending trips in {monthLabel(monthFilter)}.</div>
      )}

      {/* Archived (dismissed) tickets — recoverable */}
      <div className="pt-1">
        <button onClick={toggleArchived} className="text-sm text-muted-foreground hover:text-foreground">
          {showArchived ? '▾' : '▸'} Archived{archived ? ` (${archived.length})` : ''}
        </button>
        {showArchived && (
          <Card className="mt-2">
            <CardContent className="py-3 space-y-1">
              {archivedLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
              {!archivedLoading && archived && archived.length === 0 && (
                <div className="text-sm text-muted-foreground">Nothing archived.</div>
              )}
              {!archivedLoading && (archived || []).map(t => (
                <div key={t.syncro_ticket_id} className="flex items-center justify-between gap-2 py-1.5 border-b border-border/40 last:border-0">
                  <div className="min-w-0">
                    <div className="text-sm truncate">
                      {t.customer_name}
                      <span className="text-muted-foreground"> · #{t.number} {t.subject}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {weekday(t.created_date)} {t.created_date}{t.code ? ` · ${t.code}` : ''}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => restore(t.syncro_ticket_id)} className="shrink-0">Restore</Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SuggestedTrips;
